import React, { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box, Button, Typography, CircularProgress, Chip,
  Paper, Divider, useMediaQuery, useTheme, IconButton,
} from "@mui/material";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import DeselectIcon from "@mui/icons-material/Deselect";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

// ─── Card mobile para una fila ────────────────────────────────────────────────
// Renderiza los campos de la fila usando las columnas como definición de labels.
// Columnas con renderCell se muestran como acción si su header es corto (≤10 chars)
// o son la última columna — caso contrario se llama renderCell para obtener el valor.
const RowCard = ({ row, columns, selected, onSelect, onClick, enableCheckbox, enableSelectionClick }) => {
  // Separar columnas de acción (sin field o con field === "__check__") de columnas de datos
  const dataColumns = columns.filter(
    c => c.field && c.field !== "__check__" && !c.type?.includes("action")
  );

  // La primera columna actúa como título de la card
  const [titleCol, ...restCols] = dataColumns;

  const getCellValue = (col, row) => {
    if (col.renderCell) {
      try {
        const result = col.renderCell({ row, value: row[col.field] });
        // Si renderCell devuelve JSX lo usamos directo; si devuelve string/number lo mostramos
        return result;
      } catch {
        return row[col.field] ?? "—";
      }
    }
    if (col.valueGetter) {
      try { return col.valueGetter({ row, value: row[col.field] }); } catch { /* noop */ }
    }
    const val = row[col.field];
    return val !== undefined && val !== null && val !== "" ? String(val) : "—";
  };

  return (
    <Paper
      elevation={0}
      onClick={() => enableSelectionClick && onClick?.({ row })}
      sx={{
        border: `1.5px solid ${selected ? "#a5b4fc" : "#e2e8f0"}`,
        bgcolor: selected ? "#eef2ff" : "#fff",
        borderRadius: "12px",
        p: 2,
        mb: 1.5,
        cursor: enableSelectionClick ? "pointer" : "default",
        transition: "all 0.15s ease",
        "&:hover": enableSelectionClick
          ? { borderColor: "#a5b4fc", boxShadow: "0 4px 16px rgba(99,102,241,0.1)" }
          : {},
      }}
    >
      {/* Fila superior: título + checkbox/check */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.25 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#1e293b", flex: 1, pr: 1 }}>
          {getCellValue(titleCol, row)}
        </Typography>
        {enableCheckbox && (
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); onSelect?.(); }}
            sx={{ p: 0.25 }}
          >
            {selected
              ? <CheckCircleIcon sx={{ fontSize: 20, color: "#6366f1" }} />
              : <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: "#cbd5e1" }} />
            }
          </IconButton>
        )}
      </Box>

      <Divider sx={{ mb: 1.25, borderColor: "#f1f5f9" }} />

      {/* Grid de campos restantes */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px" }}>
        {restCols.map(col => (
          <Box key={col.field}>
            <Typography sx={{ fontSize: "0.67rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", mb: "2px" }}>
              {col.headerName || col.field}
            </Typography>
            <Box sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#1e293b" }}>
              {getCellValue(col, row)}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// ─── Toolbar de selección ─────────────────────────────────────────────────────
const SelectionToolbar = ({ selectionCount, onSelectAll, onDeselect, onConfirm }) => {
  const btnSx = (variant) => ({
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "none",
    px: 1.5, py: 0.5,
    minHeight: 32,
    boxShadow: "none",
    transition: "all 0.15s ease",
    ...(variant === "accent" && {
      bgcolor: "#6366f1", color: "#fff",
      "&:hover": { bgcolor: "#4f46e5" },
      "&:disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
    }),
    ...(variant === "outline" && {
      bgcolor: "transparent", color: "#64748b",
      border: "1.5px solid #e2e8f0",
      "&:hover": { bgcolor: "#f8fafc", borderColor: "#94a3b8" },
    }),
    ...(variant === "default" && {
      bgcolor: "#f8fafc", color: "#1e293b",
      border: "1px solid #e2e8f0",
      "&:hover": { bgcolor: "#fff", borderColor: "#94a3b8" },
    }),
  });

  return (
    <Box sx={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      mb: 1.5, p: 1.5,
      bgcolor: "#f8fafc",
      borderRadius: "10px",
      border: "1px solid #e2e8f0",
      flexWrap: "wrap", gap: 1,
    }}>
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        <Button onClick={onSelectAll} startIcon={<SelectAllIcon sx={{ fontSize: 16 }} />} sx={btnSx("default")}>
          Todos
        </Button>
        <Button onClick={onDeselect} startIcon={<DeselectIcon sx={{ fontSize: 16 }} />} sx={btnSx("outline")}>
          Ninguno
        </Button>
        <Button onClick={onConfirm} disabled={selectionCount === 0}
          startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />} sx={btnSx("accent")}>
          Confirmar
        </Button>
      </Box>
      <Chip
        label={`${selectionCount} seleccionado${selectionCount !== 1 ? "s" : ""}`}
        size="small"
        sx={{
          fontWeight: 700, fontSize: "0.72rem",
          bgcolor: selectionCount > 0 ? "#eef2ff" : "#f1f5f9",
          color: selectionCount > 0 ? "#6366f1" : "#94a3b8",
          border: `1px solid ${selectionCount > 0 ? "#c7d2fe" : "#e2e8f0"}`,
        }}
      />
    </Box>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
export const CustomDataGrid = ({
  rows,
  columns,
  pageSize,
  rowCount,
  onRowClick,
  onPageChange,
  onPageSizeChange,
  isLoading,
  onSelectionChange = false,
  onConfirmSelection = false,
  enableCheckboxSelection = false,
  enableSelectionFinder = false,
  enableSelectionClick = true,
  enablePagination = true,
}) => {
  const [selectionModel, setSelectionModel] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleSelectAll = () => {
    const allIds = rows.map(r => r.id);
    setSelectionModel(allIds);
    if (onSelectionChange) onSelectionChange(rows);
  };

  const handleDeselectAll = () => {
    setSelectionModel([]);
    if (onSelectionChange) onSelectionChange([]);
  };

  const handleConfirmSelection = () => {
    const selectedRows = rows.filter(r => selectionModel.includes(r.id));
    if (onConfirmSelection) onConfirmSelection(selectedRows);
  };

  const handleRowClick = (params) => {
    if (enableSelectionClick) {
      onRowClick?.(params);
    } else {
      const id = params.row.id;
      const next = selectionModel.includes(id)
        ? selectionModel.filter(x => x !== id)
        : [...selectionModel, id];
      setSelectionModel(next);
      if (onSelectionChange) onSelectionChange(rows.filter(r => next.includes(r.id)));
    }
  };

  const toggleCardSelect = (id) => {
    const next = selectionModel.includes(id)
      ? selectionModel.filter(x => x !== id)
      : [...selectionModel, id];
    setSelectionModel(next);
    if (onSelectionChange) onSelectionChange(rows.filter(r => next.includes(r.id)));
  };

  // ── Loading ──
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 1.5 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: "10px", bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={24} sx={{ color: "#6366f1" }} />
        </Box>
        <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8" }}>Cargando datos...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Toolbar de selección */}
      {enableSelectionFinder && (
        <SelectionToolbar
          selectionCount={selectionModel.length}
          onSelectAll={handleSelectAll}
          onDeselect={handleDeselectAll}
          onConfirm={handleConfirmSelection}
        />
      )}

      {/* ── MOBILE: cards ── */}
      {isMobile ? (
        <Box>
          {rows.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8" }}>
                No se encontraron registros
              </Typography>
            </Box>
          ) : (
            rows.map(row => (
              <RowCard
                key={row.id}
                row={row}
                columns={columns}
                selected={selectionModel.includes(row.id)}
                enableCheckbox={enableCheckboxSelection || enableSelectionFinder}
                enableSelectionClick={enableSelectionClick}
                onSelect={() => toggleCardSelect(row.id)}
                onClick={handleRowClick}
              />
            ))
          )}
        </Box>
      ) : (
        /* ── DESKTOP: DataGrid ── */
        <DataGrid
          className="datagrid"
          rows={rows}
          rowHeight={36}
          columns={columns}
          checkboxSelection={enableCheckboxSelection}
          disableSelectionOnClick={false}
          selectionModel={selectionModel}
          onRowClick={enableSelectionClick ? handleRowClick : undefined}
          onSelectionModelChange={next => {
            setSelectionModel(next);
            if (onSelectionChange) onSelectionChange(rows.filter(r => next.includes(r.id)));
          }}
          paginationMode={enablePagination ? "server" : undefined}
          pageSize={enablePagination ? pageSize : rows.length}
          rowCount={enablePagination ? rowCount : rows.length}
          hideFooterRowCount
          onPageChange={enablePagination ? p => onPageChange(p + 1) : undefined}
          onPageSizeChange={enablePagination ? s => onPageSizeChange(s) : undefined}
          autoHeight={enablePagination}
          hideFooterPagination={!enablePagination}
          components={{
            Pagination: () => null,
            NoRowsOverlay: () => (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", py: 4 }}>
                <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8" }}>
                  No se encontraron registros
                </Typography>
              </Box>
            ),
          }}
          sx={{
            border: "1.5px solid #e2e8f0",
            borderRadius: "12px",
            overflow: "hidden",
            bgcolor: "#fff",
            fontSize: "0.8rem",

            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "#f8fafc",
              borderBottom: "2px solid #e2e8f0",
              minHeight: "40px !important",
              maxHeight: "40px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              fontSize: "0.72rem",
              color: "#1e293b",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e2e8f0" },
            "& .MuiDataGrid-cell": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: "0.78rem",
              color: "#334155",
              borderBottom: "1px solid #f1f5f9",
            },
            "& .MuiDataGrid-row": {
              transition: "background-color 0.15s ease",
              "&:hover": { bgcolor: "#f8fafc" },
              "&.Mui-selected": {
                bgcolor: "#eef2ff",
                "&:hover": { bgcolor: "#eef2ff" },
              },
            },
            "& .MuiCheckbox-root": {
              color: "#cbd5e1",
              "&.Mui-checked": { color: "#6366f1" },
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
              minHeight: "40px !important",
            },
            "& .MuiTablePagination-root": {
              fontSize: "0.75rem",
              color: "#94a3b8",
            },
            "& .MuiDataGrid-virtualScroller": {
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#e2e8f0",
                borderRadius: "99px",
              },
            },
          }}
        />
      )}
    </Box>
  );
};

export default CustomDataGrid;